// File: frontend/src/pages/UserProfilePage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { mixpanelTrack } from '@/services/mixpanel';
import { fetchMyContestHistory, ApiContestHistoryEntry } from '@/api/profile';
import NotificationSettings from '@/components/common/NotificationSettings';
import HapticSettings from '@/components/common/HapticSettings';
import { updateProfile, deleteAccount, ApiError } from '@/api/auth';
import { format, parseISO, isValid } from 'date-fns';
import { suppressRejectedSessionReports } from '@/services/authSessionRecovery';

const logger = console;

const UserProfilePage: React.FC = () => {
  const { user, token, isLoading: isAuthLoading, isAuthenticated, refreshUserContext, logout } = useAuth();
  const [contestHistory, setContestHistory] = useState<ApiContestHistoryEntry[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState<boolean>(true);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [hasMoreHistory, setHasMoreHistory] = useState<boolean>(true);
  const itemsPerPage = 10;

  // Edit profile state
  const [isEditing, setIsEditing] = useState(false);
  const [editUsername, setEditUsername] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editError, setEditError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Delete account state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Track initial profile page view
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      mixpanelTrack('Viewed Profile Page', {
        'User ID': user.id,
      });
    }
  }, [isAuthenticated, user]);

  const loadContestHistory = useCallback(async (pageToLoad: number) => {
    if (!token || !isAuthenticated) {
      setHistoryError("Authentication required to view profile.");
      setIsLoadingHistory(false);
      return;
    }

    setIsLoadingHistory(true);
    if (pageToLoad === 0) {
      setContestHistory([]);
    }
    setHistoryError(null);

    try {
      const skip = pageToLoad * itemsPerPage;
      logger.log(`[UserProfilePage] Fetching contest history for page: ${pageToLoad}, skip: ${skip}, limit: ${itemsPerPage}`);
      const data = await fetchMyContestHistory(token, skip, itemsPerPage);

      mixpanelTrack('Viewed Contest History', {
        'User ID': user?.id,
        'Page Loaded': pageToLoad,
        'Items Fetched': data.length,
        'Has More': data.length >= itemsPerPage,
      });

      if (data.length === 0 && pageToLoad === 0) {
        logger.info("[UserProfilePage] No contest history found for user.");
        setHasMoreHistory(false);
      } else if (data.length < itemsPerPage) {
        logger.info("[UserProfilePage] Fetched last page of contest history.");
        setHasMoreHistory(false);
      } else {
        setHasMoreHistory(true);
      }

      setContestHistory(prevHistory => pageToLoad === 0 ? data : [...prevHistory, ...data]);
      setCurrentPage(pageToLoad);

    } catch (err: any) {
      logger.error("[UserProfilePage] Error fetching contest history:", err);
      let errMessage = "Failed to load contest history. Please try again later.";
      if (err instanceof ApiError) {
        errMessage = err.message;
      } else if (err instanceof Error) {
        errMessage = err.message;
      }
      setHistoryError(errMessage);
      mixpanelTrack('View Contest History Failed', {
        'User ID': user?.id,
        'Page Attempted': pageToLoad,
        'Error': errMessage
      });
      if (pageToLoad === 0) setContestHistory([]);
      setHasMoreHistory(false);
    } finally {
      setIsLoadingHistory(false);
    }
  }, [token, isAuthenticated, itemsPerPage, user?.id]);

  useEffect(() => {
    if (!isAuthLoading && isAuthenticated) {
      logger.log("[UserProfilePage] Auth ready, loading initial contest history (page 0).");
      loadContestHistory(0);
    } else if (!isAuthLoading && !isAuthenticated) {
      setHistoryError("Please log in to view your profile.");
      setIsLoadingHistory(false);
    }
  }, [isAuthLoading, isAuthenticated, loadContestHistory]);

  useEffect(() => {
    if (isAuthLoading || window.location.hash !== '#contest-history') return;
    const frame = window.requestAnimationFrame(() => {
      document.getElementById('contest-history')?.scrollIntoView({ block: 'start' });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [isAuthLoading]);

  const handleLoadMore = () => {
    if (!isLoadingHistory && hasMoreHistory) {
      logger.log(`[UserProfilePage] Loading more history, current page: ${currentPage}, next page: ${currentPage + 1}`);
      loadContestHistory(currentPage + 1);
    }
  };

  const handleStartEdit = () => {
    setEditUsername(user?.username || '');
    setEditEmail(user?.email || '');
    setEditError(null);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditError(null);
  };

  const handleSaveProfile = async () => {
    if (!token) return;
    setIsSaving(true);
    setEditError(null);

    const updates: Record<string, string | null> = {};
    if (editUsername !== user?.username) updates.username = editUsername;
    if (editEmail !== (user?.email || '')) updates.email = editEmail.trim() || null;

    if (Object.keys(updates).length === 0) {
      setIsEditing(false);
      setIsSaving(false);
      return;
    }

    try {
      await updateProfile(updates, token);
      await refreshUserContext();
      setIsEditing(false);
      mixpanelTrack('Profile Updated', { fields: Object.keys(updates) });
    } catch (err: any) {
      setEditError(err instanceof ApiError ? err.message : 'Failed to update profile.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!token || deleteConfirmText !== 'DELETE') return;
    setIsDeleting(true);
    setDeleteError(null);
    const releaseSessionRecovery = suppressRejectedSessionReports();
    let accountDeleted = false;

    try {
      await deleteAccount(token);
      accountDeleted = true;
      mixpanelTrack('Account Deleted');
      await logout();
    } catch (err: any) {
      setDeleteError(err instanceof ApiError ? err.message : 'Failed to delete account.');
      setIsDeleting(false);
    } finally {
      // A failed delete replays any rejected-session report. A successful
      // delete deliberately discards it because logout has already cleared
      // the now-deleted account's session.
      releaseSessionRecovery(accountDeleted);
    }
  };

  const formatDateForDisplay = (dateString: string | null | undefined): string => {
    if (!dateString) return 'N/A';
    try {
      const date = parseISO(dateString);
      return isValid(date) ? format(date, 'MMM d, yyyy') : 'Invalid Date';
    } catch (e) {
      logger.error(`Error formatting date: ${dateString}`, e);
      return 'Format Error';
    }
  };

  if (isAuthLoading) {
    return <div className="flex justify-center items-center min-h-screen bg-primary text-accent-light-blue">Loading Profile...</div>;
  }

  if (!isAuthenticated) {
    return (
        <div className="min-h-screen bg-primary p-4 md:p-8 text-gray-200 font-body text-center">
            <h1 className="text-2xl font-heading font-bold text-secondary mb-4">Access Denied</h1>
            <p className="text-gray-400">Please <Link to="/login" className="text-accent-yellow hover:underline">log in</Link> to view your profile.</p>
        </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary p-4 md:p-8 text-gray-200 font-body">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-heading font-bold text-accent-light-blue mb-6 text-center">
          MY PROFILE
        </h1>

        <section className="bg-surface-card p-6 rounded-lg shadow-xl mb-8">
          {!isEditing ? (
            <>
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-heading text-2xl text-accent-yellow">
                  {user?.username || "Your Profile"}
                </h2>
                <button
                  onClick={handleStartEdit}
                  className="text-sm text-accent-light-blue hover:text-accent-yellow transition-colors"
                >
                  Edit Profile
                </button>
              </div>
              <div className="text-sm text-gray-300 space-y-1">
                <p><span className="font-semibold text-gray-400">Phone:</span> {user?.phone_number || "N/A"}</p>
                <p><span className="font-semibold text-gray-400">Email:</span> {user?.email || "Not provided"}</p>
              </div>
            </>
          ) : (
            <>
              <h2 className="font-heading text-2xl text-accent-yellow mb-4">Edit Profile</h2>
              {editError && (
                <p className="text-secondary bg-red-900/30 p-3 rounded-md mb-4 text-sm">{editError}</p>
              )}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-400 mb-1">Username</label>
                  <input
                    type="text"
                    value={editUsername}
                    onChange={(e) => setEditUsername(e.target.value)}
                    className="w-full bg-primary border border-gray-600 rounded px-3 py-2 text-gray-200 focus:outline-none focus:border-accent-yellow"
                    maxLength={30}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-400 mb-1">Email</label>
                  <input
                    type="email"
                    value={editEmail}
                    onChange={(e) => setEditEmail(e.target.value)}
                    className="w-full bg-primary border border-gray-600 rounded px-3 py-2 text-gray-200 focus:outline-none focus:border-accent-yellow"
                    placeholder="your@email.com"
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={handleSaveProfile}
                    disabled={isSaving}
                    className="bg-accent-light-blue hover:bg-blue-500 text-primary text-sm font-bold py-2 px-4 rounded transition-colors disabled:opacity-50"
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="bg-gray-600 hover:bg-gray-500 text-gray-200 text-sm font-bold py-2 px-4 rounded transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </>
          )}
        </section>

        <NotificationSettings />
        <HapticSettings />

        <section id="contest-history" className="scroll-mt-24 bg-surface-card p-4 md:p-6 rounded-lg shadow-xl">
          <h2 className="font-heading text-2xl text-accent-yellow mb-4">Contest History</h2>
          {isLoadingHistory && contestHistory.length === 0 && (
            <p className="text-center text-gray-400 italic py-4">Loading history...</p>
          )}
          {historyError && (
            <p className="text-center text-secondary bg-red-900/30 p-3 rounded-md my-4">{historyError}</p>
          )}
          {!isLoadingHistory && !historyError && contestHistory.length === 0 && (
            <p className="text-center text-gray-400 italic py-4">No past contests found.</p>
          )}

          {contestHistory.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="border-b-2 border-gray-700 text-gray-400 uppercase text-xs tracking-wider">
                  <tr>
                    <th className="p-2 font-semibold">Date</th>
                    <th className="p-2 font-semibold">Contest Name</th>
                    <th className="p-2 font-semibold text-center">Type</th>
                    <th className="p-2 font-semibold text-right">Rank</th>
                    <th className="p-2 font-semibold text-right">Score</th>
                    <th className="p-2 font-semibold text-center">View</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {contestHistory.map((result) => (
                    <tr key={result.id} className="hover:bg-primary/30">
                      <td className="p-2 whitespace-nowrap">{formatDateForDisplay(result.contest_date)}</td>
                      <td className="p-2 truncate max-w-xs" title={result.contest_name || undefined}>{result.contest_name || 'N/A'}</td>
                      <td className="p-2 text-center">
                        <span className={`px-2 py-0.5 text-badge rounded-full font-semibold ${
                          result.is_weekend_contest
                            ? 'bg-yellow-700/70 text-yellow-200'
                            : 'bg-blue-700/70 text-blue-200'
                        }`}>
                          {result.is_weekend_contest ? 'WEEKEND' : 'DAILY'}
                        </span>
                      </td>
                      <td className="p-2 text-right font-semibold">{result.final_rank}</td>
                      <td className="p-2 text-right font-semibold text-accent-yellow">{result.final_score.toFixed(2)}</td>
                      <td className="p-2 text-center">
                        <Link
                          to={`/dfs/contest/${result.contest_id}`}
                          className="text-accent-light-blue hover:text-accent-yellow hover:underline text-xs font-semibold"
                          title={`View details for contest ${result.contest_name || result.contest_id}`}
                        >
                          Details
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!isLoadingHistory && hasMoreHistory && contestHistory.length > 0 && (
            <div className="mt-6 text-center">
              <button
                onClick={handleLoadMore}
                className="bg-secondary hover:bg-red-700 text-white text-sm font-bold py-2 px-4 rounded transition-colors"
              >
                Load More
              </button>
            </div>
          )}
           {isLoadingHistory && contestHistory.length > 0 && (
             <p className="text-center text-gray-400 italic py-4 mt-4">Loading more...</p>
           )}
        </section>

        {/* Delete Account */}
        <section className="mt-8 border border-red-800/50 rounded-lg p-6">
          <h2 className="font-heading text-lg text-secondary mb-2">Delete Account</h2>
          <p className="text-sm text-gray-400 mb-4">
            Permanently delete your account. You will be logged out immediately and will not be able to log back in.
            Your personal information and linked Google or Apple account ID will be removed from
            our systems within 14 days. We also submit your linked analytics and push data for deletion. League and contest history that other users participated in is retained in
            anonymized form. This action cannot be undone.
          </p>
          {!showDeleteConfirm ? (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="bg-red-900/50 hover:bg-red-800 text-secondary text-sm font-bold py-2 px-4 rounded transition-colors border border-red-700"
            >
              Delete My Account
            </button>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-gray-300">
                Type <span className="font-bold text-secondary">DELETE</span> to confirm:
              </p>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                className="w-full max-w-xs bg-primary border border-red-700 rounded px-3 py-2 text-gray-200 focus:outline-none focus:border-secondary"
                placeholder="Type DELETE"
              />
              {deleteError && (
                <p className="text-secondary text-sm">{deleteError}</p>
              )}
              <div className="flex gap-3">
                <button
                  onClick={handleDeleteAccount}
                  disabled={deleteConfirmText !== 'DELETE' || isDeleting}
                  className="bg-secondary hover:bg-red-700 text-white text-sm font-bold py-2 px-4 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDeleting ? 'Deleting...' : 'Confirm Delete'}
                </button>
                <button
                  onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText(''); setDeleteError(null); }}
                  className="bg-gray-600 hover:bg-gray-500 text-gray-200 text-sm font-bold py-2 px-4 rounded transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </section>

        <div className="mt-8 text-center text-xs text-gray-500">
          <Link to="/privacy" state={{ from: '/profile' }} className="hover:text-accent-light-blue underline">Privacy Policy</Link>
          <span className="mx-2">·</span>
          <Link to="/support" state={{ from: '/profile' }} className="hover:text-accent-light-blue underline">Support</Link>
        </div>
      </div>
    </div>
  );
};

export default UserProfilePage;
